import { create } from 'zustand'
import { channelApi } from '../../services/api'
import type { Message } from '../../types'

// Persistence key for the optimistic `pendingMessages` slice. We only
// persist this one bucket — the rest of the store (active channel,
// confirmed messages, unread set) is either ephemeral or refetchable
// from the server, so writing it to localStorage just adds noise.
const PENDING_LS_KEY = 'pendingMessages_v1'
// Drop persisted pendings older than this on rehydrate. After 24h the
// "Reconnecting…" affordance is no longer useful — the user has either
// typed something else or the message would already have landed via
// some other client. Keeps the localStorage blob from growing
// unbounded if the user repeatedly reloads with errored bubbles.
const PENDING_MAX_AGE_MS = 24 * 60 * 60 * 1000

// Exported so tests can pin the rehydration + expiry behaviour without
// having to reload the module. Production callers go through the
// inline call inside `create()` below.
export function __loadPersistedPendingForTests(now: number = Date.now()): Record<string, PendingMessage[]> {
  return loadPersistedPending(now)
}

function loadPersistedPending(now: number = Date.now()): Record<string, PendingMessage[]> {
  if (typeof window === 'undefined') return {}
  try {
    const raw = localStorage.getItem(PENDING_LS_KEY)
    if (!raw) return {}
    const parsed = JSON.parse(raw) as Record<string, PendingMessage[]>
    if (!parsed || typeof parsed !== 'object') return {}
    // Rehydrated entries are flipped to 'error' so the user sees a
    // clear "needs retry" state. We have no way to know whether the
    // original send actually landed on the server before reload — but
    // since the server is idempotent on `localId`, the upcoming socket
    // connect handler can retry every entry safely.
    const out: Record<string, PendingMessage[]> = {}
    for (const [key, list] of Object.entries(parsed)) {
      if (!Array.isArray(list)) continue
      // OSPROD-68: drop entries whose bucket key OR serverDmChannelId
      // still points at a `temp-…` id — those can never reconcile with
      // a server-side row, so Retry always 403s. The user gets the
      // failed bubble silently removed instead of staring at a Retry
      // button that's guaranteed to fail.
      if (key.startsWith('dm-temp-')) continue
      const fresh = list.filter((p) => {
        const ts = Date.parse(p.createdAt)
        if (!Number.isFinite(ts)) return false
        if (now - ts >= PENDING_MAX_AGE_MS) return false
        if (p.serverDmChannelId && p.serverDmChannelId.startsWith('temp-')) return false
        return true
      })
      if (fresh.length === 0) continue
      out[key] = fresh.map((p) => ({
        ...p,
        status: 'error' as const,
        errorMessage: p.errorMessage || 'Reconnecting…',
      }))
    }
    return out
  } catch {
    return {}
  }
}

function savePersistedPending(pending: Record<string, PendingMessage[]>) {
  if (typeof window === 'undefined') return
  try {
    // Drop empty buckets so the blob doesn't bloat with stale keys.
    const cleaned: Record<string, PendingMessage[]> = {}
    for (const [key, list] of Object.entries(pending)) {
      if (Array.isArray(list) && list.length > 0) cleaned[key] = list
    }
    if (Object.keys(cleaned).length === 0) {
      localStorage.removeItem(PENDING_LS_KEY)
    } else {
      localStorage.setItem(PENDING_LS_KEY, JSON.stringify(cleaned))
    }
  } catch {
    // localStorage may be full / disabled (private mode); ignore.
  }
}

// Client-First: while a message is in flight we keep a placeholder bubble
// in `pendingMessages[channelId]` keyed by `localId`. The list view renders
// these merged with confirmed messages, sorted by createdAt — so the user
// sees their text appear the instant they hit Enter. When the server
// echoes the canonical row back (via `message:new` socket / REST 201),
// `confirmPendingMessage` swaps the placeholder for the real message.
//
// Transport tells the executor which API surface to fire. Stored on the
// pending row instead of a closure so the entry is fully serialisable —
// enabling localStorage persistence + reload survival, and on-reconnect
// retry without the original component closure.
export type PendingTransport = 'socket-channel' | 'rest-channel' | 'rest-dm'

export interface PendingMessage {
  localId: string
  content: string
  // Bucket key (e.g. `channel-id` or `dm-<dmChannelId>`). Same string
  // used as the outer key in `pendingMessages`. Kept on the entry so a
  // serialized blob is self-describing on rehydrate.
  channelId: string
  // Server-side foreign key — channelId on text channels, dmChannelId
  // on DMs. The transport-specific send picks one of these.
  serverChannelId?: string
  serverDmChannelId?: string
  transport: PendingTransport
  // Telegram-style DM bubbles read these typed media slots. We carry
  // them through so an optimistic photo/voice render looks the same as
  // a real one — only the timestamp/avatar differ.
  audioUrl?: string
  audioDuration?: number
  videoUrl?: string
  videoDuration?: number
  photoUrl?: string
  photoWidth?: number
  photoHeight?: number
  stickerId?: string
  gifUrl?: string
  gifWidth?: number
  gifHeight?: number
  replyToId?: string
  attachments?: Array<{ fileName: string; fileUrl: string; fileSize: number; mimeType: string }>
  // OSPROD-66: present only for voice-channel privates. Server-side
  // validation lives in apps/server/src/lib/private-messages.ts; the
  // composer resets this to undefined after every successful send.
  recipientIds?: string[]
  // Snapshot of the author at send-time so the bubble renders even if
  // the user signs out before the confirm lands.
  author: { id: string; username: string; avatarUrl: string | null; firstName?: string | null; lastName?: string | null; position?: string | null }
  authorId: string
  createdAt: string
  // 'sending' = waiting on the server. 'error' = failed; the bubble
  // shows a Retry affordance. We don't persist 'sent' — once the real
  // row arrives we delete the pending entry entirely.
  status: 'sending' | 'error'
  errorMessage?: string
}

interface ChannelState {
  activeChannelId: string | null
  activeChannelType: 'TEXT' | 'VOICE' | null
  messages: Record<string, Message[]>
  pendingMessages: Record<string, PendingMessage[]>
  // Pure client-side unread tracking for text channels: any message:new
  // event the socket layer routes here while the user isn't viewing that
  // channel adds the channel id to this set; navigating into a channel
  // clears it. Powers the unread badge on the Business OS rail icon.
  // Server-side per-user read receipts would be more durable across
  // device switches, but this matches the current message-store
  // architecture (purely in-memory) and ships now.
  unreadChannelIds: Set<string>
  setActiveChannel: (id: string | null, type?: 'TEXT' | 'VOICE') => void
  fetchMessages: (channelId: string) => Promise<void>
  addMessage: (channelId: string, message: Message) => void
  updateMessage: (channelId: string, message: Message) => void
  deleteMessage: (channelId: string, messageId: string) => void
  setMessages: (channelId: string, messages: Message[]) => void
  markChannelUnread: (channelId: string) => void
  markChannelRead: (channelId: string) => void
  // Client-First pending lifecycle. addPendingMessage shows the bubble.
  // confirmPendingMessage replaces it with the canonical server row by
  // matching `localId` (also called from the message:new socket handler
  // when the broadcast belongs to a message we sent). failPendingMessage
  // flips the bubble to error state. markPendingSending re-arms a
  // bubble for a retry attempt. removePendingMessage is the explicit
  // dismiss / discard path.
  addPendingMessage: (channelId: string, msg: PendingMessage) => void
  confirmPendingMessage: (channelId: string, localId: string, message: Message) => boolean
  failPendingMessage: (channelId: string, localId: string, errorMessage: string) => void
  markPendingSending: (channelId: string, localId: string) => void
  removePendingMessage: (channelId: string, localId: string) => void
  // Wipe everything the store holds + the persisted blob. Called from
  // authStore.logout so a different user signing in on the same browser
  // doesn't see the previous account's messages, cached unread state,
  // or in-flight optimistic bubbles.
  resetForLogout: () => void
}

export const useChannelStore = create<ChannelState>((set, get) => ({
  activeChannelId: null,
  activeChannelType: null,
  messages: {},
  pendingMessages: loadPersistedPending(),
  unreadChannelIds: new Set<string>(),

  setActiveChannel: (id, type) => {
    set({ activeChannelId: id, activeChannelType: type || null })
    if (id) {
      // Switching INTO a channel marks it read. Voice channels never
      // accumulate unread anyway, but the unconditional clear keeps
      // logic symmetric and harmless.
      const next = new Set(get().unreadChannelIds)
      if (next.delete(id)) set({ unreadChannelIds: next })
    }
  },

  fetchMessages: async (channelId) => {
    const res = await channelApi.messages(channelId)
    // After a fetch, drop any pending bubble whose localId now appears
    // among the canonical rows — that means the original send actually
    // landed before the page reload / channel switch and we should
    // stop showing the placeholder.
    const incoming: Message[] = res.data
    const localIds = new Set<string>(incoming.map((m: any) => m.localId).filter(Boolean))
    const pending = get().pendingMessages
    let nextPending = pending
    if (localIds.size && pending[channelId]?.some((p) => localIds.has(p.localId))) {
      nextPending = {
        ...pending,
        [channelId]: pending[channelId].filter((p) => !localIds.has(p.localId)),
      }
    }
    set({
      messages: { ...get().messages, [channelId]: incoming },
      pendingMessages: nextPending,
    })
  },

  addMessage: (channelId, message) => {
    const current = get().messages[channelId] || []
    if (current.some((m) => m.id === message.id)) return
    // If this message echoes one of our optimistic bubbles, swallow the
    // pending entry so the user doesn't see the bubble twice while the
    // socket and REST paths race.
    const localId = (message as any).localId
    let pending = get().pendingMessages
    if (localId && pending[channelId]?.some((p) => p.localId === localId)) {
      pending = {
        ...pending,
        [channelId]: pending[channelId].filter((p) => p.localId !== localId),
      }
    }
    set({
      messages: { ...get().messages, [channelId]: [...current, message] },
      pendingMessages: pending,
    })
  },

  updateMessage: (channelId, message) => {
    const current = get().messages[channelId] || []
    set({
      messages: {
        ...get().messages,
        [channelId]: current.map((m) => (m.id === message.id ? message : m)),
      },
    })
  },

  deleteMessage: (channelId, messageId) => {
    const current = get().messages[channelId] || []
    set({
      messages: {
        ...get().messages,
        [channelId]: current.filter((m) => m.id !== messageId),
      },
    })
  },

  setMessages: (channelId, messages) => {
    // Same dedup as fetchMessages: any pending bubble whose localId is
    // present in the incoming rows is dropped — the bubble's job is
    // done, the canonical row will render in its place.
    const localIds = new Set<string>((messages as any[]).map((m: any) => m.localId).filter(Boolean))
    const pending = get().pendingMessages
    let nextPending = pending
    if (localIds.size && pending[channelId]?.some((p) => localIds.has(p.localId))) {
      nextPending = {
        ...pending,
        [channelId]: pending[channelId].filter((p) => !localIds.has(p.localId)),
      }
    }
    // OSPROD-141 follow-up: defensive id-dedupe + chronological sort on
    // the incoming list. User reported chat content duplicating on
    // tab-switch+return (each visit: array grew by exactly the chat
    // size, with duplicates wedged out-of-order). Without a clean repro
    // on my side, I couldn't pin the append path — so collapse
    // duplicates by id AND re-sort by createdAt asc at the store
    // boundary. Idempotent: a clean asc-ordered list passes through
    // unchanged. The server already returns asc, so the sort is a
    // no-op on the happy path.
    const seen = new Set<string>()
    const deduped: Message[] = []
    for (const m of messages) {
      if (!m?.id || seen.has(m.id)) continue
      seen.add(m.id)
      deduped.push(m)
    }
    deduped.sort((a, b) => {
      const ta = a?.createdAt ? Date.parse(a.createdAt) : 0
      const tb = b?.createdAt ? Date.parse(b.createdAt) : 0
      if (ta !== tb) return ta - tb
      // Stable tiebreak — id keeps two messages at the same millisecond
      // in a deterministic order across renders.
      return (a.id ?? '').localeCompare(b.id ?? '')
    })
    set({
      messages: { ...get().messages, [channelId]: deduped },
      pendingMessages: nextPending,
    })
  },

  markChannelUnread: (channelId) => {
    const next = new Set(get().unreadChannelIds)
    next.add(channelId)
    set({ unreadChannelIds: next })
  },

  markChannelRead: (channelId) => {
    const next = new Set(get().unreadChannelIds)
    if (next.delete(channelId)) set({ unreadChannelIds: next })
  },

  addPendingMessage: (channelId, msg) => {
    const current = get().pendingMessages[channelId] || []
    if (current.some((p) => p.localId === msg.localId)) return
    set({
      pendingMessages: { ...get().pendingMessages, [channelId]: [...current, msg] },
    })
  },

  confirmPendingMessage: (channelId, localId, message) => {
    const pending = get().pendingMessages[channelId] || []
    const had = pending.some((p) => p.localId === localId)
    const messages = get().messages[channelId] || []
    const alreadyHasReal = messages.some((m) => m.id === message.id)
    set({
      pendingMessages: {
        ...get().pendingMessages,
        [channelId]: pending.filter((p) => p.localId !== localId),
      },
      messages: alreadyHasReal
        ? get().messages
        : { ...get().messages, [channelId]: [...messages, message] },
    })
    return had
  },

  failPendingMessage: (channelId, localId, errorMessage) => {
    const pending = get().pendingMessages[channelId] || []
    set({
      pendingMessages: {
        ...get().pendingMessages,
        [channelId]: pending.map((p) =>
          p.localId === localId
            ? { ...p, status: 'error', errorMessage }
            : p
        ),
      },
    })
  },

  markPendingSending: (channelId, localId) => {
    const pending = get().pendingMessages[channelId] || []
    set({
      pendingMessages: {
        ...get().pendingMessages,
        [channelId]: pending.map((p) =>
          p.localId === localId
            ? { ...p, status: 'sending', errorMessage: undefined }
            : p
        ),
      },
    })
  },

  removePendingMessage: (channelId, localId) => {
    const pending = get().pendingMessages[channelId] || []
    set({
      pendingMessages: {
        ...get().pendingMessages,
        [channelId]: pending.filter((p) => p.localId !== localId),
      },
    })
  },

  resetForLogout: () => {
    if (typeof window !== 'undefined') {
      try { localStorage.removeItem(PENDING_LS_KEY) } catch { /* ignore */ }
    }
    set({
      activeChannelId: null,
      activeChannelType: null,
      messages: {},
      pendingMessages: {},
      unreadChannelIds: new Set<string>(),
    })
  },
}))

// Persist pendingMessages to localStorage on every change so a reload
// doesn't drop the user's in-flight or errored bubbles. Only pending
// state goes through here — confirmed messages are already on the
// server and refetched on the next visit.
let lastPersisted = useChannelStore.getState().pendingMessages
useChannelStore.subscribe((state) => {
  if (state.pendingMessages !== lastPersisted) {
    lastPersisted = state.pendingMessages
    savePersistedPending(state.pendingMessages)
  }
})
